/**
 * Clerk · Verify Returns
 * Lists returns pending clerk verification.
 * Actions: [ VERIFY RETURN ] (upload photos → clerk_verified) or Reject with reason.
 * Reuses:
 *   GET  /api/clerk/returns
 *   POST /api/upload/clerk-verification   (multipart, files=[])
 *   POST /api/clerk/verify-return          { return_id, status, verification_photos, rejection_reason }
 */
import React, { useMemo, useRef, useState } from 'react';
import { PackageCheck, Upload, X, Camera } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import {
  EmptyState, StatusPill, SearchInput, Modal,
  formatShortTime, shortRef, conditionLabel, conditionVariant,
} from '../ui';

const REJECT_REASONS = [
  { value: 'damaged',           label: 'Damaged' },
  { value: 'quantity_mismatch', label: 'Quantity mismatch' },
  { value: 'product_mismatch',  label: 'Product mismatch' },
];

export default function VerifyPage({ ctx }) {
  const { pendingReturns, refresh, showToast } = ctx;
  const [query, setQuery] = useState('');

  const [verifyItem, setVerifyItem] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState('damaged');
  const [rejecting, setRejecting] = useState(false);

  const [zoomPhoto, setZoomPhoto] = useState(null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pendingReturns;
    return pendingReturns.filter((r) => {
      const hay = [
        r.student_name, r.student_category, r.id, r.notes,
        ...(r.items || []).flatMap((it) => [it.component_name, it.brand_name, it.condition]),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [pendingReturns, query]);

  const openVerify = (r) => {
    setVerifyItem(r);
    setPhotoFiles([]);
    setPhotoPreviews([]);
  };
  const closeVerify = () => {
    if (uploading) return;
    setVerifyItem(null);
    setPhotoFiles([]);
    setPhotoPreviews([]);
  };

  const handleFilesPicked = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        showToast(`${f.name} exceeds 5 MB. Please choose smaller photos.`, 'bad');
        return;
      }
    }
    setPhotoFiles(files);
    const previews = [];
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews.push(reader.result);
        if (previews.length === files.length) setPhotoPreviews([...previews]);
      };
      reader.readAsDataURL(f);
    });
  };

  const submitVerify = async () => {
    if (!verifyItem || uploading) return;
    if (!photoFiles.length) {
      showToast('Upload at least one verification photo.', 'bad');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      photoFiles.forEach((f) => fd.append('files', f));
      const upload = await apiClient.post(endpoints.uploads.clerkVerification, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const photos = upload.data?.photo_urls
        || upload.data?.file_urls
        || (upload.data?.photo_url ? [upload.data.photo_url] : []);
      await apiClient.post(endpoints.returns.clerkVerify, {
        return_id: verifyItem.id,
        status: 'clerk_verified',
        verification_photos: photos,
      });
      showToast(`Return ${shortRef(verifyItem.id, 'RET')} verified · sent to admin.`, 'ok');
      closeVerify();
      await refresh.returns();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Verification failed.', 'bad');
    } finally {
      setUploading(false);
    }
  };

  const submitReject = async () => {
    if (!rejectItem || rejecting) return;
    setRejecting(true);
    try {
      await apiClient.post(endpoints.returns.clerkVerify, {
        return_id: rejectItem.id,
        status: 'rejected',
        rejection_reason: rejectReason,
      });
      showToast(`Return ${shortRef(rejectItem.id, 'RET')} rejected.`, 'ok');
      setRejectItem(null);
      await refresh.returns();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Rejection failed.', 'bad');
    } finally {
      setRejecting(false);
    }
  };

  const buildPhotoUrl = (photo) => {
    if (!photo) return '';
    if (photo.startsWith('http')) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  return (
    <div data-testid="cp-verify-page">
      <div className="cp-eyebrow">Returns Queue</div>
      <h1 className="cp-page-title">Verify Returns</h1>
      <p className="cp-page-lede">
        Physically inspect each returned component. Approve with verification photos to send
        to admin for final approval, or reject if the item can't be accepted.
      </p>

      <div className="cp-toolbar cp-mt-5">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search by student, component, brand or notes…"
          testId="cp-verify-search"
        />
        <div className="cp-subtle">
          {rows.length} {rows.length === 1 ? 'return' : 'returns'}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={PackageCheck}
          title="No returns awaiting verification"
          message="Student-submitted returns appear here for your physical inspection."
          testId="cp-verify-empty"
        />
      ) : (
        <div className="cp-table-wrap" data-testid="cp-verify-table">
          <table className="cp-table">
            <thead>
              <tr>
                <th style={{ width: '18%' }}>Student</th>
                <th style={{ width: '14%' }}>Return</th>
                <th>Items & Condition</th>
                <th style={{ width: '14%' }}>Submitted</th>
                <th style={{ width: '20%' }} className="cp-td-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`cp-verify-row-${r.id}`}>
                  <td>
                    <div className="cp-td-strong">{r.student_name || '—'}</div>
                    <div className="cp-td-meta">{r.student_category || ''}</div>
                  </td>
                  <td>
                    <div className="cp-td-mono">{shortRef(r.id, 'RET')}</div>
                    <div className="cp-td-meta">
                      {(r.items?.length || 0)} {(r.items?.length || 0) === 1 ? 'item' : 'items'}
                    </div>
                  </td>
                  <td>
                    <ul className="cp-item-lines">
                      {(r.items || []).map((it, i) => (
                        <li key={i} style={{ flexWrap: 'wrap' }}>
                          <span className="cp-item-qty">×{it.quantity}</span>
                          <strong>{it.component_name}</strong>
                          {it.brand_name && <span className="cp-item-brand">{it.brand_name}</span>}
                          {it.size && <span className="cp-item-brand">{it.size}</span>}
                          {it.condition && (
                            <span style={{ marginLeft: 4 }}>
                              <StatusPill variant={conditionVariant(it.condition)}>
                                {conditionLabel(it.condition)}
                              </StatusPill>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {r.notes && (
                      <div className="cp-td-meta" style={{ marginTop: 8 }}>
                        <em style={{ fontStyle: 'normal', color: 'var(--diis-ink-muted)' }}>
                          “{r.notes}”
                        </em>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="cp-td-meta cp-td-strong" style={{ marginTop: 0 }}>
                      {formatShortTime(r.created_at)}
                    </div>
                    {(r.return_photos?.length || 0) > 0 && (
                      <div className="cp-td-meta">{r.return_photos.length} photo(s)</div>
                    )}
                  </td>
                  <td className="cp-td-actions">
                    <button
                      type="button"
                      className="cp-btn cp-btn-ghost cp-btn-sm"
                      onClick={() => { setRejectItem(r); setRejectReason('damaged'); }}
                      data-testid={`cp-verify-reject-${r.id}`}
                    >Reject</button>
                    <button
                      type="button"
                      className="cp-btn cp-btn-accent cp-btn-sm"
                      style={{ marginLeft: 8 }}
                      onClick={() => openVerify(r)}
                      data-testid={`cp-verify-approve-${r.id}`}
                    >
                      Verify Return
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Verify modal */}
      <Modal
        open={!!verifyItem}
        onClose={closeVerify}
        title="Verify Return"
        subtitle="Upload photos of the cleaned and inspected items before sending to admin."
        wide
        testId="cp-verify-modal"
        footer={(
          <>
            <button
              type="button"
              className="cp-btn cp-btn-ghost"
              onClick={closeVerify}
              disabled={uploading}
              data-testid="cp-verify-modal-cancel"
            >Cancel</button>
            <button
              type="button"
              className="cp-btn cp-btn-accent"
              onClick={submitVerify}
              disabled={uploading || photoFiles.length === 0}
              data-testid="cp-verify-modal-submit"
            >
              {uploading ? 'Uploading…' : 'Verify & Send to Admin'}
            </button>
          </>
        )}
      >
        {verifyItem && (
          <>
            <dl className="cp-def-list">
              <div><dt>Student</dt><dd>{verifyItem.student_name}</dd></div>
              <div><dt>Category</dt><dd>{verifyItem.student_category || '—'}</dd></div>
              <div><dt>Return</dt><dd className="cp-mono-num">{shortRef(verifyItem.id, 'RET')}</dd></div>
              <div><dt>Submitted</dt><dd>{formatShortTime(verifyItem.created_at)}</dd></div>
            </dl>

            <div className="cp-mt-5">
              <div className="cp-field-label">Returned items</div>
              <ul className="cp-item-lines">
                {(verifyItem.items || []).map((it, i) => (
                  <li key={i}>
                    <span className="cp-item-qty">×{it.quantity}</span>
                    <strong>{it.component_name}</strong>
                    {it.brand_name && <span className="cp-item-brand">{it.brand_name}</span>}
                    {it.condition && (
                      <span style={{ marginLeft: 4 }}>
                        <StatusPill variant={conditionVariant(it.condition)}>
                          {conditionLabel(it.condition)}
                        </StatusPill>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {verifyItem.return_photos?.length > 0 && (
              <div className="cp-mt-5">
                <div className="cp-field-label">Student-submitted photos</div>
                <div className="cp-photo-grid">
                  {verifyItem.return_photos.map((p, i) => (
                    <div
                      key={i}
                      className="cp-photo-thumb"
                      onClick={() => setZoomPhoto(buildPhotoUrl(p))}
                      data-testid={`cp-verify-student-photo-${i}`}
                    >
                      <img src={buildPhotoUrl(p)} alt={`Student return photo ${i + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="cp-mt-5">
              <div className="cp-field-label">Verification photos (after cleaning)</div>
              <label className="cp-file-drop" data-testid="cp-verify-drop">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFilesPicked}
                  data-testid="cp-verify-file-input"
                />
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <Camera size={18} />
                  <span>{photoFiles.length
                    ? `${photoFiles.length} photo(s) ready — click to change`
                    : 'Click to add photos · JPG/PNG · up to 5 MB each'}</span>
                </div>
              </label>

              {photoPreviews.length > 0 && (
                <div className="cp-photo-grid cp-mt-3">
                  {photoPreviews.map((src, i) => (
                    <div key={i} className="cp-photo-thumb">
                      <img src={src} alt={`Verification preview ${i + 1}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Reject modal */}
      <Modal
        open={!!rejectItem}
        onClose={() => !rejecting && setRejectItem(null)}
        title="Reject Return"
        subtitle="Select the primary reason. Admin will be notified."
        testId="cp-verify-reject-modal"
        footer={(
          <>
            <button
              type="button"
              className="cp-btn cp-btn-ghost"
              onClick={() => setRejectItem(null)}
              disabled={rejecting}
              data-testid="cp-verify-reject-cancel"
            >Cancel</button>
            <button
              type="button"
              className="cp-btn cp-btn-danger"
              onClick={submitReject}
              disabled={rejecting}
              data-testid="cp-verify-reject-submit"
            >
              {rejecting ? 'Rejecting…' : 'Confirm Reject'}
            </button>
          </>
        )}
      >
        {rejectItem && (
          <>
            <dl className="cp-def-list">
              <div><dt>Student</dt><dd>{rejectItem.student_name}</dd></div>
              <div><dt>Return</dt><dd className="cp-mono-num">{shortRef(rejectItem.id, 'RET')}</dd></div>
            </dl>
            <div className="cp-mt-5">
              <div className="cp-field-label">Reason</div>
              <div className="cp-chip-row">
                {REJECT_REASONS.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    className={`cp-chip${rejectReason === r.value ? ' is-active' : ''}`}
                    onClick={() => setRejectReason(r.value)}
                    data-testid={`cp-verify-reject-chip-${r.value}`}
                  >{r.label}</button>
                ))}
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Zoom photo */}
      {zoomPhoto && (
        <div
          className="cp-modal-scrim"
          onClick={() => setZoomPhoto(null)}
          data-testid="cp-photo-zoom"
        >
          <div
            className="cp-modal cp-modal-wide"
            style={{ background: 'transparent', border: 0, boxShadow: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button
                type="button"
                className="cp-btn cp-btn-ghost cp-btn-sm"
                onClick={() => setZoomPhoto(null)}
              ><X size={14} /> Close</button>
            </div>
            <img
              src={zoomPhoto}
              alt="Zoomed"
              style={{ width: '100%', borderRadius: 12, background: '#000' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
